import {
  Activity,
  Bell,
  ChevronDown,
  Globe,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Menu,
  Plus,
  Search,
  Settings,
  User,
  Wrench,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { MonitorListItem } from "@shared/types";
import { StatusDot } from "@/components/status-dot";
import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { logout } from "@/lib/api";
import { useAuthStatus, useGroups, useMonitors } from "@/lib/queries";
import { cn, formatLatency } from "@/lib/utils";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/notifications", label: "Notifications", icon: Bell },
  { to: "/status-pages", label: "Status Pages", icon: Globe },
  { to: "/maintenance", label: "Maintenance", icon: Wrench },
  { to: "/settings", label: "Settings", icon: Settings },
] as const;

interface MonitorSection {
  key: string;
  groupId: number | null;
  name: string | null;
  monitors: MonitorListItem[];
}

function useMonitorSections(search: string): { sections: MonitorSection[]; loading: boolean } {
  const { data: monitors, isLoading } = useMonitors();
  const { data: groups } = useGroups();

  const sections = useMemo(() => {
    const list = monitors ?? [];
    const needle = search.trim().toLowerCase();
    const filtered = needle ? list.filter(m => m.name.toLowerCase().includes(needle)) : list;

    const byGroup = new Map<number | null, MonitorListItem[]>();
    for (const m of filtered) {
      const key = m.groupId;
      const bucket = byGroup.get(key);
      if (bucket) bucket.push(m);
      else byGroup.set(key, [m]);
    }

    const out: MonitorSection[] = [];
    for (const g of groups ?? []) {
      const items = byGroup.get(g.id);
      if (items?.length) out.push({ key: `g-${g.id}`, groupId: g.id, name: g.name, monitors: items });
      byGroup.delete(g.id);
    }
    // Any remaining buckets (ungrouped or groups not yet loaded)
    for (const [groupId, items] of byGroup) {
      out.push({
        key: groupId === null ? "ungrouped" : `g-${groupId}`,
        groupId,
        name: groupId === null ? null : `Group ${groupId}`,
        monitors: items,
      });
    }
    return out;
  }, [monitors, groups, search]);

  return { sections, loading: isLoading };
}

function MonitorRow({ monitor }: { monitor: MonitorListItem }) {
  return (
    <NavLink
      to={`/monitors/${monitor.id}`}
      className={({ isActive }) =>
        cn(
          "group flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors",
          isActive ? "bg-accent text-accent-foreground" : "text-foreground/80 hover:bg-accent/60",
        )
      }
    >
      <StatusDot status={monitor.currentStatus} paused={!monitor.active} pulse />
      <span className="min-w-0 flex-1 truncate">{monitor.name}</span>
      {monitor.active && monitor.currentStatus === 1 && monitor.lastLatency != null && (
        <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
          {formatLatency(monitor.lastLatency)}
        </span>
      )}
    </NavLink>
  );
}

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const [search, setSearch] = useState("");
  const { sections, loading } = useMonitorSections(search);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const toggle = (key: string) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="flex h-full flex-col" onClick={onNavigate ? e => {
      // close the mobile sheet when a link inside was clicked
      if ((e.target as HTMLElement).closest("a")) onNavigate();
    } : undefined}>
      <div className="flex items-center gap-2 px-4 py-4">
        <div className="flex size-8 items-center justify-center rounded-lg bg-status-up/15">
          <Activity className="size-4.5 text-status-up" />
        </div>
        <span className="text-base font-semibold tracking-tight">Vigil</span>
      </div>

      <div className="space-y-2 px-3">
        <Button asChild className="w-full justify-start gap-2">
          <Link to="/monitors/new">
            <Plus className="size-4" /> New Monitor
          </Link>
        </Button>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search monitors…"
            className="h-8 pl-8 text-sm"
          />
        </div>
      </div>

      <ScrollArea className="mt-3 flex-1 px-3">
        <div className="space-y-4 pb-4">
          {loading && (
            <div className="space-y-2 px-1">
              {Array.from({ length: 6 }, (_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          )}
          {!loading && sections.length === 0 && (
            <p className="px-2 py-6 text-center text-xs text-muted-foreground">
              {search ? "No monitors match your search." : "No monitors yet — create your first one."}
            </p>
          )}
          {sections.map(section => (
            <div key={section.key}>
              {section.name !== null || sections.length > 1 ? (
                <button
                  type="button"
                  onClick={() => toggle(section.key)}
                  className="flex w-full items-center gap-1 px-2 pb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground hover:text-foreground"
                >
                  <ChevronDown
                    className={cn("size-3 transition-transform", collapsed.has(section.key) && "-rotate-90")}
                  />
                  {section.name ?? "Ungrouped"}
                  <span className="ml-auto tabular-nums">{section.monitors.length}</span>
                </button>
              ) : null}
              {!collapsed.has(section.key) && (
                <div className="space-y-0.5">
                  {section.monitors.map(m => (
                    <MonitorRow key={m.id} monitor={m} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </ScrollArea>

      <Separator />
      <nav className="space-y-0.5 p-3">
        {NAV_ITEMS.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors",
                isActive ? "bg-accent text-accent-foreground" : "text-foreground/70 hover:bg-accent/60",
              )
            }
          >
            <item.icon className="size-4" />
            {item.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

function SummaryChips() {
  const { data: monitors } = useMonitors();
  if (!monitors) return null;
  const up = monitors.filter(m => m.active && m.currentStatus === 1).length;
  const down = monitors.filter(m => m.active && m.currentStatus === 0).length;
  const paused = monitors.filter(m => !m.active).length;

  return (
    <div className="hidden items-center gap-2 sm:flex">
      <span className="inline-flex items-center gap-1.5 rounded-full border border-status-up/30 bg-status-up/10 px-2.5 py-1 text-xs font-medium text-status-up">
        <span className="size-1.5 rounded-full bg-status-up" /> {up} up
      </span>
      <span
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium",
          down > 0
            ? "border-status-down/40 bg-status-down/15 text-status-down"
            : "border-border bg-muted/50 text-muted-foreground",
        )}
      >
        <span className={cn("size-1.5 rounded-full", down > 0 ? "animate-pulse bg-status-down" : "bg-muted-foreground/50")} />
        {down} down
      </span>
      {paused > 0 && (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-2.5 py-1 text-xs font-medium text-muted-foreground">
          {paused} paused
        </span>
      )}
    </div>
  );
}

export function AppLayout() {
  const { data: auth } = useAuthStatus();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    await queryClient.invalidateQueries();
    navigate("/login");
  };

  return (
    <div className="flex h-dvh overflow-hidden bg-background">
      <aside className="hidden w-72 shrink-0 border-r md:block">
        <SidebarContent />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-3 border-b px-4">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" aria-label="Open menu">
                <Menu className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">
              <SidebarContent onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>

          <div className="flex-1" />
          <SummaryChips />
          <ThemeToggle />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Account">
                <User className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel className="truncate">{auth?.username ?? "Admin"}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/settings")}>
                <KeyRound className="size-4" /> Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} variant="destructive">
                <LogOut className="size-4" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
